import { getLinkedItems } from '@/components/contexts/KontentHomeConfigProvider'
import type { IContentItemsContainer } from '@kontent-ai/delivery-sdk'
import type {
	PrescriptionGroup,
	PrescriptionWork,
	PrescriptionWorkBundle,
} from '../types'

const TYPE_PRESCRIPTION_WORK_BUNDLE = 'prescription_work_bundle'

const collectAllInnerWorks = (
	group: PrescriptionGroup,
	linkedItems: IContentItemsContainer,
): PrescriptionWork[] => {
	const children =
		getLinkedItems(group.elements.prescription_work, linkedItems) ?? []

	const works: PrescriptionWork[] = []

	for (const child of children) {
		if (child.system.type === TYPE_PRESCRIPTION_WORK_BUNDLE) {
			const bundle = child as PrescriptionWorkBundle
			const innerWorks =
				getLinkedItems(
					bundle.elements.prescription_work,
					linkedItems,
				) ?? []
			for (const innerWork of innerWorks) {
				works.push(innerWork)
			}
		} else {
			// Standalone PrescriptionWork.
			works.push(child as PrescriptionWork)
		}
	}

	return works
}

const countDistinctTypeCodenames = (works: PrescriptionWork[]): number => {
	const typeCodenames = new Set<string>()
	for (const work of works) {
		const codename = work.elements.prescription_type.value[0]?.codename
		if (codename) {
			typeCodenames.add(codename)
		}
	}
	return typeCodenames.size
}

/**
 * Whether to render the per-work `(type)` label for every work in a group.
 * True when the group is mixed-type (≥ 2 distinct prescription types) OR the
 * group contains exactly one work. Bundle inner works are counted,
 * so a lone bundle (2 works) is NOT "single". Studies
 * are rendered by `<PrescriptionStudy>` and never reach this path.
 */
export const shouldShowGroupTypeLabel = (
	group: PrescriptionGroup,
	linkedItems: IContentItemsContainer,
): boolean => {
	const works = collectAllInnerWorks(group, linkedItems)
	if (works.length === 1) return true
	return countDistinctTypeCodenames(works) >= 2
}
